import { useCallback } from "react";
import {
  createBypassEmail,
  displayEmail,
  isSignedIn,
  useBypassEmail,
  useSession,
} from "@jkast90/kast-common/auth";
import { STORAGE_KEYS } from "../../_common/constants";
import { getAuthEnabled } from "../../_common/settings";
import { session } from "../session";

// With auth off (local dev) there is no session: the email the developer is
// acting as is the signed-in user (kast-common's bypass email store, kept
// under the key pages read too, as with a real session). The route gate's
// BypassEmailGate asks for it.
export const bypassEmail = createBypassEmail({
  storageKey: STORAGE_KEYS.USER_EMAIL,
});

/** The signed-in user's email now, for callbacks that are not components. */
export function currentUserEmail(): string | null {
  return getAuthEnabled()
    ? displayEmail(session.getState().user) || null
    : bypassEmail.get() || null;
}

/**
 * Who is signed in: the shared session (`useSession` holds the last signed-in
 * state while a sign-out leaves the page, so nothing flashes signed out), or
 * with auth off the bypass email. `setUserEmail` is for auth off only.
 */
export function useAuth() {
  const state = useSession();
  const bypass = useBypassEmail(bypassEmail) || null;
  const authOn = getAuthEnabled();
  const userEmail = authOn ? displayEmail(state.user) || null : bypass;
  const isAuthenticated = authOn ? isSignedIn(state) : !!bypass;
  return {
    userEmail,
    /** The Cognito user id (`sub`); null with auth off. */
    userId: authOn ? state.user?.sub || null : null,
    isAuthenticated,
    /** The stored session is still being read or refreshed. */
    isLoading: authOn && state.status === "loading",
    /** The signed-in user; null while a stored session is still loading, though its email is known. */
    user:
      isAuthenticated && userEmail ? { id: userEmail, email: userEmail } : null,
    setUserEmail: useCallback((email: string) => bypassEmail.set(email), []),
  };
}
